import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { api, unwrap } from "../../api/client";
import { qk } from "../../api/keys";
import type { UrlPatch } from "../url";
import { FrameController, type PlayerState } from "./controller";

/** One controller per media + fps; the <video> binds through the returned ref callback. */
export function usePlayer(opts: { mediaKey: string | number | null; fps: number; durationMs?: number | null;
  frameCount?: number | null }) {
  const { mediaKey, fps, durationMs, frameCount } = opts;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const ctrl = useMemo(() => new FrameController({ fps, durationMs, frameCount }), [mediaKey, fps]);
  useEffect(() => () => ctrl.destroy(), [ctrl]);
  useEffect(() => ctrl.setDuration(durationMs ?? null, frameCount ?? null), [ctrl, durationMs, frameCount]);
  const videoRef = useCallback((el: HTMLVideoElement | null) => ctrl.attach(el), [ctrl]);
  return { ctrl, videoRef };
}

export function usePlayerState(ctrl: FrameController): PlayerState {
  return useSyncExternalStore(ctrl.subscribe, ctrl.getState);
}

/** Subscribe to one derived primitive (re-renders only when it changes). */
export function usePlayerSelect<T extends string | number | boolean | null>(ctrl: FrameController,
  sel: (s: PlayerState) => T): T {
  return useSyncExternalStore(ctrl.subscribe, () => sel(ctrl.getState()));
}

export type MediaInfo = { fps: number | null; vcodec: string | null; durationMs: number | null;
  frames: number | null; mime: string | null };

/** fps / codec / duration of a media file from /api/media/{id}/meta (ffprobe summary). */
export function useMediaInfo(mediaId: number | null | undefined) {
  return useQuery({
    queryKey: qk.entity("media", mediaId ?? 0, "meta"),
    enabled: mediaId != null,
    staleTime: 60_000,
    queryFn: async (): Promise<MediaInfo> => {
      const m = await unwrap(api.GET("/api/media/{media_id}/meta", { params: { path: { media_id: mediaId as number } } }));
      const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? v : null);
      return { fps: num(m.meta.fps), vcodec: typeof m.meta.vcodec === "string" ? m.meta.vcodec : null,
        durationMs: num(m.meta.duration_ms), frames: num(m.meta.frames), mime: m.mime };
    },
  });
}

/** Keep the player and the URL in step: t (written while paused), rate, loop, in, out. */
export function useUrlPlayerSync(ctrl: FrameController, url: {
  int: (k: string) => number | null; flag: (k: string) => boolean; str: (k: string) => string | null;
  set: (p: UrlPatch) => void;
}, opts: { writeTime?: boolean } = {}) {
  const writeTime = opts.writeTime ?? true;
  const initial = useRef<{ ctrl: FrameController | null }>({ ctrl: null });
  const tParam = url.int("t");
  useEffect(() => {
    if (initial.current.ctrl === ctrl) return;
    initial.current.ctrl = ctrl;
    if (tParam != null && tParam > 0) ctrl.seekMs(tParam);
  }, [ctrl, tParam]);

  const rate = Number(url.str("rate") ?? "1");
  useEffect(() => ctrl.setRate(Number.isFinite(rate) && rate > 0 ? rate : 1), [ctrl, rate]);
  const inMs = url.int("in");
  const outMs = url.int("out");
  const loop = url.flag("loop");
  useEffect(() => ctrl.setLoop({ inMs, outMs, on: loop }), [ctrl, inMs, outMs, loop]);

  const set = url.set;
  useEffect(() => {
    if (!writeTime) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let last = ctrl.getState().tMs;
    const unsub = ctrl.subscribe(() => {
      const s = ctrl.getState();
      if (s.playing || s.tMs === last) return;
      last = s.tMs;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => set({ t: s.tMs || null }), 250);
    });
    return () => {
      unsub();
      if (timer) clearTimeout(timer);
    };
  }, [ctrl, set, writeTime]);
}
